import { Injectable, signal, computed, effect, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ProductModel } from '../models/product.model';

type CartItem = { product: ProductModel; qty: number };

@Injectable({ providedIn: 'root' })
export class CartService {
  private readonly KEY = 'cart.v1';

  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  private itemsSig = signal<CartItem[]>(this.load());

  items = computed(() => this.itemsSig());
  count = computed(() => this.itemsSig().reduce((a, c) => a + c.qty, 0));
  total = computed(() => this.itemsSig().reduce((a, c) => a + (c.product.price || 0) * c.qty, 0));

  constructor() {
    // Persistir sólo en navegador
    if (this.isBrowser) {
      effect(() => {
        try {
          localStorage.setItem(this.KEY, JSON.stringify(this.itemsSig()));
        } catch {}
      });
    }
  }

  add(p: ProductModel, qty = 1) {
    if (p.salePolicy === 'QUOTE') return; // no compra directa
    const list = [...this.itemsSig()];
    const i = list.findIndex(x => x.product.id === p.id);
    if (i >= 0) list[i] = { ...list[i], qty: list[i].qty + qty };
    else list.push({ product: p, qty });
    this.itemsSig.set(list);
  }

  remove(id: string) {
    this.itemsSig.set(this.itemsSig().filter(x => x.product.id !== id));
  }

  setQty(id: string, qty: number) {
    if (qty <= 0) return this.remove(id);
    this.itemsSig.set(this.itemsSig().map(x => x.product.id === id ? { ...x, qty } : x));
  }

  clear() { this.itemsSig.set([]); }

  private load(): CartItem[] {
    if (!this.isBrowser) return []; // en servidor devolvemos vacío
    try { return JSON.parse(localStorage.getItem(this.KEY) || '[]'); }
    catch { return []; }
  }
}
